import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BTN, BTN_PRIMARY } from "@/components/postbacks/shared";

function MacroChips({ macros, onPick }) {
  return (
    <div className="flex flex-wrap gap-1 mt-2" data-testid="postback-macros">
      {macros.map((m) => (
        <button key={m.key} type="button" title={m.label} onClick={() => onPick(`{${m.key}}`)}
          data-testid={`postback-macro-${m.key}`}
          className="border border-zinc-400 bg-zinc-50 px-1.5 py-0.5 text-[11px] font-mono hover:border-black hover:bg-white">
          {`{${m.key}}`}
        </button>
      ))}
    </div>
  );
}

export function EventBoxes({ events, value, onChange }) {
  const toggle = (k) => onChange(value.includes(k) ? value.filter((e) => e !== k) : [...value, k]);
  return (
    <div className="grid sm:grid-cols-2 gap-1.5 mt-1" data-testid="postback-events">
      {events.map((e) => (
        <label key={e.key} className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={value.includes(e.key)} onChange={() => toggle(e.key)}
            data-testid={`postback-event-${e.key}`} className="accent-black" />
          {e.label}
        </label>
      ))}
    </div>
  );
}

// Create or edit a postback link. `row` null = new.
export function PostbackDialog({ row, meta, onClose, onSaved }) {
  const [name, setName] = useState(row?.name || "");
  const [url, setUrl] = useState(row?.url || "");
  const [events, setEvents] = useState(row?.events || ["lead", "sold"]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const urlRef = useRef(null);
  const insert = (token) => {
    const el = urlRef.current;
    const at = el ? el.selectionStart ?? url.length : url.length;
    setUrl(url.slice(0, at) + token + url.slice(at));
    setError("");
  };
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const body = { name: name.trim(), url: url.trim(), events };
      const { data } = row ? await api.patch(`/postbacks/${row.id}`, body) : await api.post("/postbacks", body);
      onSaved(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === "string" ? d : "Check the name, link and moments");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="rounded-none border-2 border-black sm:max-w-xl" data-testid="postback-dialog">
        <form onSubmit={save}>
          <DialogHeader>
            <DialogTitle className="font-chivo text-xl font-black">{row ? "Edit postback" : "New postback"}</DialogTitle>
            <DialogDescription>
              We call this link from our server each time one of the moments below happens.
              {meta.needs_approval ? " An admin approves new links and link changes before they go live." : " It goes live as soon as you save it."}
            </DialogDescription>
          </DialogHeader>
          <label className="label-mono block mt-4 mb-1" htmlFor="postback-name">Name</label>
          <input id="postback-name" value={name} onChange={(e) => { setName(e.target.value); setError(""); }} maxLength={80} required
            placeholder="e.g. Voluum conversions" data-testid="postback-name-input"
            className="border border-black px-3 py-2 w-full text-sm" />
          <label className="label-mono block mt-4 mb-1" htmlFor="postback-url">Postback link</label>
          <textarea id="postback-url" ref={urlRef} value={url} onChange={(e) => { setUrl(e.target.value); setError(""); }} required rows={3}
            placeholder="https://your-tracker.com/postback?cid={click_id}&event={event}" data-testid="postback-url-input"
            className="border border-black px-3 py-2 w-full text-xs font-mono" />
          <div className="text-[11px] text-zinc-600 mt-1">Tap a placeholder to add it. We fill it in when we send.</div>
          <MacroChips macros={meta.macros} onPick={insert} />
          <div className="label-mono mt-4">Send it when</div>
          <EventBoxes events={meta.events} value={events} onChange={(v) => { setEvents(v); setError(""); }} />
          {error && <div className="text-xs text-red-700 mt-3" data-testid="postback-error">{error}</div>}
          <DialogFooter className="gap-2 mt-5">
            <button type="button" onClick={onClose} className={BTN} data-testid="postback-cancel">Cancel</button>
            <button type="submit" disabled={busy || !events.length} className={BTN_PRIMARY} data-testid="postback-save">
              {busy ? "Saving…" : "Save postback"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
